<script setup lang="ts">
import { BaseInput, Button, IconButton, isFinite, Tooltip, TooltipIfEllipsis, useHover, useTranslation } from '@bluebrick/saas-ui'
import { storeToRefs } from 'pinia'
import { computed, onMounted, watch, watchEffect, toRefs } from 'vue'
import Telemetry from '@/common/components/Telemetry.vue'
import WarningIcon from '@/common/components/WarningIcon.vue'
import { hasOverlap, getRangeDescription, haveSameDates, isCooldownEvent, haveSameName } from '@/common/utils'
import { isDepthRecommendationAvailable } from '@/features/eventDetails/eventDetailsUtils'
import { useEventDetailsStore } from '@/features/eventDetails/useEventDetailsStore'
import { useEventsStore } from '@/features/events/useEventsStore'
import { useLoadersStore } from '@/features/loaders/useLoadersStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { type EventData, EventType, type StoreData } from '@/types'

const props = defineProps<{
	product: string
	uniqueSkuId: string
	discountDepth: number
	readonly?: boolean
}>()

const { readonly } = toRefs(props)

const {
	recommendedDiscountDepth,
	store,
	startDate,
	storeSKUs,
	isUpdateEvent,
	sourceEvent,
	eventIntervalUTC,
	sourceEventsCooldowns,
	eventDatesDraft,
	products,
	canCalculateEventParameters,
	discountConfig
} = storeToRefs(useEventDetailsStore())
const loadersStore = useLoadersStore()
const { unselectSKU } = useEventsStore()
const { storeEvents } = storeToRefs(useEventsStore())
const { trackEvent } = useTelemetry()
const { t } = useTranslation()
const { isHover, onMouseEnter, onMouseLeave } = useHover()

const recommendedDepth = computed(() => recommendedDiscountDepth.value.get(props.uniqueSkuId))

const isRecommendationAvailable = computed(() => {
	if (!store.value?.id || !storeEvents.value.has(store.value.id) || !startDate.value) {
		return false
	}

	return isDepthRecommendationAvailable(
		storeEvents.value.get(store.value.id) as StoreData,
		props.product,
		startDate.value,
		sourceEvent.value?.id ?? null
	)
})

const isRecommendedDepthVisible = computed(() => recommendedDepth.value !== undefined && isRecommendationAvailable.value)

const isDepthDifferentThanRecommended = computed(
	() => Number.isFinite(recommendedDepth.value) && Number.isFinite(props.discountDepth) && recommendedDepth.value !== props.discountDepth
)

const unavailableRecommendationTooltipContent = computed(() => {
	if (!isRecommendationAvailable.value) {
		return 'Sorry, but at the moment we can only show discount recommendations for the nearest occurring event.'
	}

	if (recommendedDepth.value === undefined) {
		return 'No depth recommendation available for this product.'
	}

	return ''
})

const emit = defineEmits<{
	'update:discountDepth': [value: number]
	'update:canSetupProduct': [value: boolean]
	applyToAll: [value: number]
	showProductHistory: [value: void]
}>()

const discount = computed({
	get: () => String(props.discountDepth),
	set: (val) => {
		if (val) {
			updateDiscountDepth(Number(val))
		}
	}
})

const updateDiscountDepth = (value: number) => {
	trackEvent('app', 'Side panel | Set discound depth.', { value })
	emit('update:discountDepth', value)
}

//remove item from product options
const deleteItem = () => {
	if (store.value) {
		unselectSKU(props.uniqueSkuId)
	}
}

// when creating a new event, we have to check is it colliding with some other important events in timeline
function isCollidingEventType(event: EventData) {
	return [EventType.Discount, EventType.Cooldown].includes(event.event_type)
}

// checks if given event is one of source event's cooldowns
function isOwnCooldown(event: EventData) {
	return isCooldownEvent(event) && sourceEventsCooldowns.value.some((sourceEventsCooldown) => haveSameDates(event, sourceEventsCooldown))
}

function isSameAsSourceEventInEditMode(event: EventData) {
	return isUpdateEvent.value && sourceEvent.value && haveSameDates(event, sourceEvent.value) && haveSameName(event, sourceEvent.value)
}

// determines if there are any interrupting events
const interruptingEvents = computed<EventData[]>(() => {
	const sku = storeSKUs.value.find((sku) => sku.unique_sku_id === props.uniqueSkuId)

	if (!sku || !sku.events || !eventIntervalUTC.value?.isValid) {
		return []
	}

	return sku.events.filter(
		(skuEvent) =>
			isCollidingEventType(skuEvent) &&
			!isOwnCooldown(skuEvent) &&
			!isSameAsSourceEventInEditMode(skuEvent) &&
			skuEvent.hash !== sourceEvent.value?.hash &&
			hasOverlap(skuEvent, eventDatesDraft.value)
	)
})

const eventInterrupts = computed(() => interruptingEvents.value.length > 0)

// if there is any interruption in events - return message about it
const firstInterruptingEventRangeDescription = computed(() => {
	if (!eventInterrupts.value || !store.value?.timeZone) {
		return ''
	}

	return getRangeDescription(
		interruptingEvents.value[0].start_date.setZone(store.value.timeZone),
		interruptingEvents.value[0].end_date.setZone(store.value.timeZone)
	)
})

const invalidDiscount = computed(
	() =>
		!isFinite(discountConfig.value.minDepth) ||
		!isFinite(discountConfig.value.maxDepth) ||
		props.discountDepth < discountConfig.value.minDepth ||
		props.discountDepth > discountConfig.value.maxDepth
)

function showHistory() {
	trackEvent('user', 'Opened product history.', { store: store.value?.displayName })

	emit('showProductHistory')
}

function useRecommendation() {
	setRecommendedDiscount()

	trackEvent('user', 'Used indiebi discount recommendation.', { store: store.value?.displayName, value: recommendedDepth.value })
}

function setRecommendedDiscount() {
	if (recommendedDepth.value !== undefined && isRecommendationAvailable.value) {
		discount.value = String(recommendedDepth.value)
	}
}

const discountOtherThanRecommended = computed(
	() =>
		isRecommendationAvailable.value &&
		!(eventInterrupts.value || invalidDiscount.value) &&
		recommendedDepth.value !== undefined &&
		discount.value !== String(recommendedDepth.value)
)

watchEffect(() => {
	emit('update:canSetupProduct', !eventInterrupts.value && !invalidDiscount.value)
})

watch(recommendedDepth, () => {
	if (!isUpdateEvent.value && !readonly.value) {
		setRecommendedDiscount()
	}
})

onMounted(() => {
	if (!isUpdateEvent.value && !readonly.value) {
		setRecommendedDiscount()
	}
})
</script>

<template>
	<!-- Style of the grid (columns) is inherited from the parent (ProductOptions.vue) -->
	<div
		:class="[
			'grid grid-cols-subgrid',
			{
				'col-span-4': canCalculateEventParameters,
				'col-span-3': !canCalculateEventParameters
			}
		]"
		@mouseenter="onMouseEnter"
		@mouseleave="onMouseLeave"
	>
		<div class="font-body-s flex flex-1 items-center">
			<IconButton v-if="!readonly" size="s" icon="close" class="mr-01" @click.stop="deleteItem"> </IconButton>

			<TooltipIfEllipsis :max-width="14" :tooltip-text="props.product" placement="top">
				<span
					:class="[
						'font-body-s',
						{
							'text-message-warning': discountOtherThanRecommended,
							'text-message-error': eventInterrupts || invalidDiscount
						}
					]"
				></span>

				{{ props.product }}
			</TooltipIfEllipsis>
		</div>

		<div class="flex items-center">
			<div class="relative">
				<BaseInput v-model="discount" size="s" class="discountInput" type="number" :readonly="readonly" custom-width maxlength="2" />

				<!--
				This outrageous 1px is needed to fine-tune the alignment of absolutely-positioned "%" sign
				-->
				<div class="font-body-m absolute right-02 top-[1px]">%</div>

				<div v-if="isHover && !readonly && !invalidDiscount && products.length > 1" class="absolute left-[-2.25rem] top-0">
					<Tooltip :content="t('productsOptions.setCurrentAllTooltip', { discount })">
						<Telemetry name="Apply given depth to all products" :payload="{ value: discount }">
							<Button
								class="applyToAllButton"
								size="xs"
								icon="check-all"
								variant="quaternary"
								@click.stop="emit('applyToAll', props.discountDepth)"
							></Button>
						</Telemetry>
					</Tooltip>
				</div>
			</div>
		</div>

		<div v-if="canCalculateEventParameters && readonly">-</div>

		<div v-else-if="canCalculateEventParameters" class="font-body-m flex items-center justify-center">
			<Tooltip v-if="isRecommendedDepthVisible && isDepthDifferentThanRecommended" :content="t('productsOptions.setRecommendationTooltip')">
				<Button :loading="loadersStore.isActive('maxDiscountDepth')" size="xs" variant="quaternary" @click="useRecommendation">{{
					`${t('productsOptions.set')} ${recommendedDepth}%`
				}}</Button>
			</Tooltip>

			<span v-else-if="isRecommendedDepthVisible">{{ recommendedDepth }} %</span>

			<Tooltip v-else :content="unavailableRecommendationTooltipContent"> ? </Tooltip>
		</div>

		<div class="cursor-pointer" @click="showHistory">
			<Tooltip :content="t('productsOptions.viewHistoryTooltip')">
				<Button size="xs" variant="quaternary" icon="calendar-clock-outline"></Button>
			</Tooltip>
		</div>

		<div
			v-if="!readonly"
			:class="[
				'font-caption',
				{
					'col-span-4': canCalculateEventParameters,
					'col-span-3': !canCalculateEventParameters
				}
			]"
		>
			<div v-if="invalidDiscount" class="text-message-error">
				Discount depth has to be between {{ discountConfig.minDepth ?? '...' }}% and {{ discountConfig.maxDepth ?? '...' }}%.
			</div>

			<div v-if="eventInterrupts" class="text-message-error">
				<div>Unavailable for the selected dates.</div>

				<div v-if="firstInterruptingEventRangeDescription" class="text-ink-60">
					(conflicts with "{{ interruptingEvents[0].name }}": {{ firstInterruptingEventRangeDescription }} )
				</div>
			</div>

			<div v-if="discountOtherThanRecommended" class="flex items-center">
				<WarningIcon class="mr-03 text-strong" />

				<div class="text-message-warning">
					We recommend <span class="font-semibold">{{ recommendedDepth }}%</span> discount for this event.
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.discountInput {
	width: 3rem;

	:deep(.inputWrapper > input) {
		// space for the "%" sign
		padding-right: 1rem;
		text-align: right;

		/* Tweaks to avoid showing up/down arrows in the number input */
		appearance: textfield; /* non-Firefox */
		-moz-appearance: textfield; /* Firefox */

		&::-webkit-inner-spin-button,
		&::-webkit-outer-spin-button {
			-webkit-appearance: none; /* Chrome, Safari, Edge */
		}
	}
}

.productOption {
	display: grid;
	grid-template-columns: 1fr 2.5rem 4rem 2.5rem;
	gap: 1rem;
}

.applyToAllButton {
	&:hover {
		:deep(svg) {
			fill: theme('colors.primary.100');
		}
	}
}
</style>
